import { useState, useEffect } from 'preact/hooks';
import { bookmarks } from '../state/bookmarks-store.js';
import { cards, dueCards, ensureCard, review, removeCard } from '../state/memorization-store.js';

type Props = {
  onClose: () => void;
};

/**
 * Spaced-repetition flashcard mode. Pulls verses from bookmarks
 * (anything you cared enough to bookmark is fair game) and quizzes
 * with fill-in-the-blank — show the verse reference, then "Reveal"
 * the verse text + Forgot / Good buttons.
 */
export function MemorizationPanel({ onClose }: Props) {
  // Make sure every bookmark has a corresponding card.
  useEffect(() => {
    for (const b of bookmarks.peek()) ensureCard(b.ref);
  }, []);

  const due = dueCards();
  const all = Object.keys(cards.value);
  const [revealed, setRevealed] = useState(false);
  const [doneCount, setDoneCount] = useState(0);
  const currentRef = due[0] ?? null;
  const card = currentRef ? bookmarks.peek().find((b) => b.ref === currentRef) : null;

  useEffect(() => {
    setRevealed(false);
  }, [currentRef]);

  useEffect(() => {
    const onKey = (e: KeyboardEvent): void => {
      if (e.key === 'Escape') onClose();
    };
    window.addEventListener('keydown', onKey);
    return () => window.removeEventListener('keydown', onKey);
  }, [onClose]);

  const handle = (outcome: 'forgot' | 'good'): void => {
    if (!currentRef) return;
    review(currentRef, outcome);
    setDoneCount((n) => n + 1);
  };

  return (
    <div class="memorization-backdrop" onClick={onClose} role="dialog" aria-modal="true">
      <div class="memorization" onClick={(e) => e.stopPropagation()}>
        <header class="memorization-header">
          <span class="memorization-title">Memorize</span>
          <button type="button" class="memorization-close" onClick={onClose} aria-label="Close">×</button>
        </header>
        <p class="memorization-progress">
          {due.length === 0
            ? `All caught up. ${all.length} cards in deck.`
            : `${due.length} due · ${doneCount} done this session`}
        </p>
        {!card ? (
          <div class="memorization-empty">
            {all.length === 0 ? (
              <>
                <p>No bookmarks yet.</p>
                <p>Bookmark verses you want to memorize and they'll show up here.</p>
              </>
            ) : (
              <p>Come back later — your next card is due in a bit.</p>
            )}
          </div>
        ) : (
          <div class="memorization-card">
            <p class="memorization-ref">
              {card.bookName} {card.chapter}:{card.verseNumber}
            </p>
            {!revealed ? (
              <button
                type="button"
                class="memorization-reveal"
                onClick={() => setRevealed(true)}
              >
                Reveal
              </button>
            ) : (
              <>
                <p class="memorization-text">"{card.text}"</p>
                <div class="memorization-actions">
                  <button
                    type="button"
                    class="memorization-btn memorization-btn-forgot"
                    onClick={() => handle('forgot')}
                  >
                    Forgot
                  </button>
                  <button
                    type="button"
                    class="memorization-btn memorization-btn-good"
                    onClick={() => handle('good')}
                  >
                    Good
                  </button>
                </div>
              </>
            )}
            <button
              type="button"
              class="memorization-remove"
              onClick={() => {
                removeCard(card.ref);
              }}
            >
              Remove from deck
            </button>
          </div>
        )}
      </div>
    </div>
  );
}
